import React from 'react';
import { ArrowLeft, Undo2, Redo2, RefreshCw, Play, Pause, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { BookmarkMenu } from './BookmarkMenu';
import { RecentEditsMenu } from './RecentEditsMenu';
import { getTerm } from '@/lib/branding';

/**
 * Top section of the middle column: back button, title, undo/redo,
 * auto-refresh picker, bookmarks menu, name/description inputs, mode toggle.
 */
export default function BuilderHeader({
  editReportId, designerMode, setDesignerMode,
  confirmNavigation, navigate,
  undo, redo, undoStack, redoStack, revertTo,
  autoRefreshInterval, setAutoRefreshInterval,
  autoRunPaused, toggleAutoRunPaused, staleCount = 0, runStaleVisuals,
  bookmarkMenuOpen, setBookmarkMenuOpen,
  bookmarks, setBookmarks, reportId,
  crossFilter, setCrossFilter,
  slicerSelections, setSlicerSelections,
  globalFilterValues, setGlobalFilterValues,
  paramValues, setParamValues,
  reportName, setReportName, setSavedClean, onLabelChange,
  reportDesc, setReportDesc,
}) {
  const title = editReportId
    ? (designerMode === 'report' ? 'Edit Report' : 'Edit Dashboard')
    : (designerMode === 'report' ? 'Report Builder' : 'Dashboard Builder');

  return (
    <div className="p-3 border-b border-border/50">
      <div className="flex items-center gap-2 mb-2">
        <Button
          variant="ghost" size="icon"
          onClick={() => confirmNavigation(() => navigate('/dashboards'))}
          className="rounded-lg w-7 h-7"
          data-testid="report-builder-back"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h2 className="text-xs font-heading font-bold text-foreground">{title}</h2>
        <div className="flex items-center gap-1 ml-auto">
          <Button variant="ghost" size="icon" onClick={undo} disabled={undoStack.length === 0} className="w-6 h-6 text-muted-foreground" title="Undo" data-testid="undo-btn"><Undo2 className="w-3 h-3" /></Button>
          <Button variant="ghost" size="icon" onClick={redo} disabled={redoStack.length === 0} className="w-6 h-6 text-muted-foreground" title="Redo" data-testid="redo-btn"><Redo2 className="w-3 h-3" /></Button>
          <RecentEditsMenu undoStack={undoStack} revertTo={revertTo} />
          <div className="h-4 w-px bg-border/30 mx-0.5" />
          <div className="flex items-center rounded-md bg-muted/40 p-0.5" title="Auto-run mode">
            <button
              onClick={() => { if (autoRunPaused) toggleAutoRunPaused(); }}
              className={`flex items-center gap-0.5 text-[8px] px-1.5 py-0.5 rounded transition-colors ${!autoRunPaused ? 'bg-primary/20 text-primary font-medium' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="autorun-live-btn"
              title="Live: every field change re-queries automatically"
            ><Play className="w-2.5 h-2.5" />Live</button>
            <button
              onClick={() => { if (!autoRunPaused) toggleAutoRunPaused(); }}
              className={`flex items-center gap-0.5 text-[8px] px-1.5 py-0.5 rounded transition-colors ${autoRunPaused ? 'bg-amber-500/20 text-amber-500 font-medium' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="autorun-paused-btn"
              title="Paused: edits are queued; press Run to refresh changed visuals"
            ><Pause className="w-2.5 h-2.5" />Paused</button>
          </div>
          {autoRunPaused && (
            <Button
              variant="ghost" size="sm"
              onClick={() => runStaleVisuals && runStaleVisuals()}
              disabled={staleCount === 0}
              className="h-6 px-1.5 text-[8px] gap-0.5 text-amber-500 hover:text-amber-600 disabled:text-muted-foreground/50"
              data-testid="autorun-run-btn"
              title={staleCount ? `Refresh ${staleCount} changed visual${staleCount === 1 ? '' : 's'} (⌘/Ctrl+Enter)` : 'Nothing to run'}
            ><Zap className="w-2.5 h-2.5" />Run{staleCount > 0 ? ` (${staleCount})` : ''}</Button>
          )}
          <div className="h-4 w-px bg-border/30 mx-0.5" />
          <Select value={String(autoRefreshInterval)} onValueChange={v => setAutoRefreshInterval(Number(v))}>
            <SelectTrigger className="!h-6 !text-[8px] !rounded !bg-transparent !border-0 w-auto gap-1 text-muted-foreground hover:text-foreground px-1" title="Auto-refresh interval">
              <RefreshCw className={`w-3 h-3 ${autoRefreshInterval > 0 ? 'text-primary animate-spin' : ''}`} style={autoRefreshInterval > 0 ? { animationDuration: '3s' } : undefined} />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">No Refresh</SelectItem>
              <SelectItem value="30">30 seconds</SelectItem>
              <SelectItem value="60">1 minute</SelectItem>
              <SelectItem value="300">5 minutes</SelectItem>
              <SelectItem value="900">15 minutes</SelectItem>
            </SelectContent>
          </Select>
          <BookmarkMenu
            open={bookmarkMenuOpen} setOpen={setBookmarkMenuOpen}
            bookmarks={bookmarks} setBookmarks={setBookmarks}
            reportId={reportId}
            crossFilter={crossFilter} setCrossFilter={setCrossFilter}
            slicerSelections={slicerSelections} setSlicerSelections={setSlicerSelections}
            globalFilterValues={globalFilterValues} setGlobalFilterValues={setGlobalFilterValues}
            paramValues={paramValues} setParamValues={setParamValues}
          />
        </div>
      </div>
      <Input
        data-testid="report-name-input"
        placeholder={`${getTerm('insights', 'singular')} name`}
        value={reportName}
        onChange={e => {
          setReportName(e.target.value);
          setSavedClean(false);
          if (onLabelChange) onLabelChange(e.target.value || 'Untitled');
        }}
        className="rounded-lg bg-muted/50 border-border/50 text-sm h-8 mb-1.5"
      />
      <div className="flex gap-1 mb-1.5">
        <button
          onClick={() => { if (designerMode !== 'dashboard' && !window.confirm('Switching to Dashboard will change the canvas layout and some report-only formatting may be lost. Continue?')) return; setDesignerMode('dashboard'); }}
          className={`flex-1 text-[9px] py-1 rounded-md transition-colors ${designerMode === 'dashboard' ? 'bg-primary/20 text-primary font-medium' : 'text-muted-foreground hover:bg-muted/20'}`}
          data-testid="mode-dashboard"
        >Dashboard</button>
        <button
          onClick={() => { if (designerMode !== 'report' && !window.confirm('Switching to Report will change the canvas layout and some dashboard-only formatting may be lost. Continue?')) return; setDesignerMode('report'); }}
          className={`flex-1 text-[9px] py-1 rounded-md transition-colors ${designerMode === 'report' ? 'bg-primary/20 text-primary font-medium' : 'text-muted-foreground hover:bg-muted/20'}`}
          data-testid="mode-report"
        >Report</button>
      </div>
      <Input
        placeholder="Description"
        value={reportDesc}
        onChange={e => setReportDesc(e.target.value)}
        className="rounded-lg bg-muted/50 border-border/50 text-[10px] h-7"
      />
    </div>
  );
}
